import { Component, OnInit } from '@angular/core';
import { KPIService } from '../../../services';
import _ from 'lodash';
import * as ChartDataLabels from 'chartjs-plugin-datalabels';
import moment from 'moment';

export enum LiveFeedOptionEnum {
  Daily = 1,
  Weekly = 2,
  Monthly = 3,
  Quarterly,
}

export enum MetricDisplayStyle {
  Number,
  Currency,
  Percentage,
}

@Component({
  selector: 'dashboard-engineius-kpi',
  styleUrls: ['./dashboard-engineius-kpi.component.scss'],
  templateUrl: './dashboard-engineius-kpi.component.html',
})
export class DashboardEngineiusKPIComponent implements OnInit {
  constructor(protected kpiService: KPIService) {}

  public liveFeedOptionEnum = LiveFeedOptionEnum;

  baseStepSize = 500;
  isLoading = false;
  isLoadingLiveFeed = false;
  csvDownloading = false;

  greyText: string = 'rgba(51, 51, 51, 1)';

  monthDates: string[]; // used for chart labels and array indices

  completedTotals: any[];
  cancelledTotals: any[];

  // first dimension is indexed by ChartData enum (i.e. totalsChartData[ChartData.CompletedMoves])
  totalsChartData: number[][];

  monthlyMetrics: {
    groupName: string;
    metrics: {
      chartData: any;
      title: string;
      data: {}[]; // per month values
      tooltip?: string;
    }[]; // total moves, perfect moves etc.
  }[];

  dataBorderColor = 'rgba(20, 20, 20, 0.7)';
  dataBorderWidth = 1;

  baseChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        top: 25,
      },
    },
    legend: {
      position: 'bottom',
    },
    elements: {
      line: {
        tension: 0,
      },
    },
    scales: {
      yAxes: [
        {
          ticks: {
            beginAtZero: true,
            padding: 15,
          },
        },
      ],
      xAxes: [
        {
          ticks: {
            padding: 10,
          },
        },
      ],
    },
    plugins: {
      datalabels: {
        align: 'center',
        anchor: 'center',
        borderRadius: 4,
        backgroundColor: 'transparent',
        color: this.greyText,
        font: {
          weight: 'bold',
        },
      },
    },
  };

  currencyChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        top: 25,
      },
    },
    legend: {
      position: 'bottom',
      labels: {
        fontColor: 'rgb(0, 0, 0)',
      },
    },
    elements: {
      line: {
        tension: 0,
      },
    },
    scales: {
      yAxes: [
        {
          ticks: {
            beginAtZero: true,
            padding: 15,
            callback: function (value, index, values) {
              let currency =
                '£' +
                (Math.abs(value) > 999
                  ? (Math.sign(value) * (Math.abs(value) / 1000)).toFixed(1) + 'k'
                  : (Math.sign(value) * Math.abs(value)).toFixed(2));
              return currency;
            },
          },
        },
      ],
      xAxes: [
        {
          ticks: {
            padding: 10,
          },
        },
      ],
    },
    plugins: {
      datalabels: {
        render: 'percentage',
        align: 'end',
        anchor: 'end',
        borderRadius: 4,
        backgroundColor: 'transparent',
        font: {
          weight: 'bold',
          size: 12,
        },
        formatter: (value, ctx) => {
          let sum = 0;
          let dataArr = ctx.chart.data.datasets[0].data;
          dataArr.map((data) => {
            sum += data;
          });
          let currency =
            '£' +
            (Math.abs(value) > 999
              ? (Math.sign(value) * (Math.abs(value) / 1000)).toFixed(1) + 'k'
              : (Math.sign(value) * Math.abs(value)).toFixed(2));

          ctx.negative = value < 0;

          return currency;
        },
        color: (context) => {
          // Change the label text color based on our new `hovered` context value.
          return context.negative ? 'red' : this.greyText;
        },
      },
    },
    tooltips: {
      callbacks: {
        label: function (tooltipItem, data) {
          let index = tooltipItem.index;
          let setIndex = tooltipItem.datasetIndex;
          let currentValue = data.datasets[setIndex].data[index];

          return (
            '£' +
            (Math.abs(currentValue) > 999
              ? (Math.sign(currentValue) * (Math.abs(currentValue) / 1000)).toFixed(1) + 'k'
              : (Math.sign(currentValue) * Math.abs(currentValue)).toFixed(2))
          );
        },
        title: function (tooltipItem, data) {
          return data.datasets[tooltipItem[0].datasetIndex].label;
        },
      },
    },
  };

  percentageChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        top: 25,
      },
    },
    legend: {
      position: 'bottom',
    },
    elements: {
      line: {
        tension: 0,
      },
    },
    scales: {
      yAxes: [
        {
          ticks: {
            //stepSize: 5,
            beginAtZero: true,
            padding: 5,
            callback: function (value, index, values) {
              return value + '%';
            },
          },
        },
      ],
      xAxes: [
        {
          ticks: {
            padding: 10,
          },
        },
      ],
    },
    plugins: {
      datalabels: {
        render: 'percentage',
        align: 'center',
        anchor: 'end',
        borderRadius: 4,
        backgroundColor: 'teal',
        color: 'white',
        font: {
          weight: 'bold',
        },
      },
    },
  };

  stackedChartOptions = {
    responsive: false,
    maintainAspectRatio: false,
    layout: {
      padding: {
        top: 25,
      },
    },
    legend: {
      position: 'bottom',
    },
    scales: {
      yAxes: [
        {
          ticks: {
            //stepSize: this.baseStepSize,
            beginAtZero: true,
            padding: 15,
          },
          stacked: false,
        },
      ],
      xAxes: [
        {
          stacked: true,
          ticks: {
            padding: 10,
          },
        },
      ],
    },
    plugins: {
      datalabels: {
        color: this.greyText,
        font: {
          weight: 'bold',
        },
        formatter: (value, ctx) => {
          return value;
        },
      },
    },
  };

  percentageTooltipChartOptions = {
    responsive: false,
    maintainAspectRatio: false,
    layout: {
      padding: {
        top: 25,
      },
    },
    legend: {
      position: 'bottom',
    },
    elements: {
      line: {
        tension: 0,
      },
    },
    scales: {
      yAxes: [
        {
          ticks: {
            //stepSize: this.baseStepSize,
            beginAtZero: true,
            padding: 5,
            callback: function (value, index, values) {
              return value + '%';
            },
          },
        },
      ],
      xAxes: [
        {
          ticks: {
            padding: 10,
          },
        },
      ],
    },
    tooltips: {
      callbacks: {
        label: function (tooltipItem, data) {
          let index = tooltipItem.index;
          let setIndex = tooltipItem.datasetIndex;
          let currentValue = data.datasets[setIndex].data[index];

          // total = completed moves
          let total = data.datasets[0].data[index];

          if (setIndex == 0) {
            if (total % 1 !== 0) {
              return total + '%';
            } else {
              return total;
            }
          }

          //data.datasets.forEach(function (el) {
          //    total = total + el.data[index];
          //});
          let percentage = parseFloat(((currentValue / total) * 100).toFixed(1));
          return currentValue + ' (' + percentage + '%)';
        },
        title: function (tooltipItem, data) {
          return data.datasets[tooltipItem[0].datasetIndex].label;
        },
      },
    },
    plugins: {
      datalabels: {
        render: 'percentage',
        align: 'end',
        anchor: 'end',
        borderRadius: 4,
        backgroundColor: 'transparent',
        color: this.greyText,
        font: {
          weight: 'bold',
          size: 12,
        },
        formatter: (value, ctx) => {
          return isNaN(value) ? '' : value.toFixed(1) + '%';
        },
      },
    },
  };

  twoSetChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        top: 25,
      },
    },
    legend: {
      position: 'bottom',
    },
    elements: {
      line: {
        tension: 0,
      },
    },
    scales: {
      yAxes: [
        {
          ticks: {
            //stepSize: this.baseStepSize,
            beginAtZero: true,
            padding: 15,
            //fontColor: this.greyText,
            //fontStyle: 'bold',
            offset: true,
            callback: function (value, index, values) {
              return value;
            },
          },
          type: 'linear', // only linear but allow scale type registration. This allows extensions to exist solely for log scale for instance
          display: true,
          position: 'right',
          id: 'y-axis-2',
        },
        {
          ticks: {
            beginAtZero: true,
            padding: 15,
            //fontColor: this.greyText,
            //fontStyle: 'bold',
            offset: true,
            callback: function (value, index, values) {
              return value;
            },
          },
          gridLines: {
            display: false,
          },
          type: 'linear', // only linear but allow scale type registration. This allows extensions to exist solely for log scale for instance
          display: true,
          position: 'left',
          id: 'y-axis-1',
        },
      ],
      xAxes: [
        {
          ticks: {
            padding: 10,
          },
        },
      ],
    },
  };

  twoSetOneScaleChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        top: 25,
      },
    },
    legend: {
      position: 'bottom',
    },
    elements: {
      line: {
        tension: 0,
      },
    },
    scales: {
      yAxes: [
        {
          ticks: {
            //stepSize: this.baseStepSize,
            beginAtZero: true,
            padding: 15,
            //fontColor: this.greyText,
            fontStyle: 'bold',
            offset: true,
          },
          type: 'linear', // only linear but allow scale type registration. This allows extensions to exist solely for log scale for instance
          display: true,
        },
      ],
      xAxes: [
        {
          ticks: {
            padding: 10,
          },
        },
      ],
    },
  };

  twoSetChartPercentageOptions = {
    responsive: true,
    maintainAspectRatio: false,
    tooltips: {
      callbacks: {
        label: function (tooltipItem, data) {
          let index = tooltipItem.index;
          let setIndex = tooltipItem.datasetIndex;
          let currentValue = data.datasets[setIndex].data[index];

          return isNaN(currentValue) ? '' : currentValue + '%';
        },
        title: function (tooltipItem, data) {
          return data.datasets[tooltipItem[0].datasetIndex].label;
        },
      },
    },
    layout: {
      padding: {
        top: 25,
      },
    },
    legend: {
      position: 'bottom',
    },
    elements: {
      line: {
        tension: 0,
      },
    },
    scales: {
      yAxes: [
        {
          ticks: {
            //stepSize: this.baseStepSize,
            beginAtZero: true,
            padding: 15,
            //fontColor: 'green',
            //fontStyle: 'bold',
            offset: true,
            callback: function (value, index, values) {
              return value + '%';
            },
          },
          type: 'linear', // only linear but allow scale type registration. This allows extensions to exist solely for log scale for instance
          display: true,
          position: 'right',
          id: 'y-axis-2',
        },
        {
          ticks: {
            beginAtZero: true,
            padding: 15,
            //fontColor: 'red',
            //fontStyle: 'bold',
            offset: true,
            callback: function (value, index, values) {
              return value + '%';
            },
          },
          gridLines: {
            display: false,
          },
          type: 'linear', // only linear but allow scale type registration. This allows extensions to exist solely for log scale for instance
          display: true,
          position: 'left',
          id: 'y-axis-1',
        },
      ],
      xAxes: [
        {
          ticks: {
            padding: 10,
          },
        },
      ],
    },
    plugins: {
      datalabels: {
        render: 'percentage',
        align: 'end',
        anchor: 'end',
        borderRadius: 4,
        backgroundColor: 'transparent',
        color: this.greyText,
        font: {
          weight: 'bold',
          size: 12,
        },
        formatter: (value, ctx) => {
          return isNaN(value) ? '' : value.toFixed(1) + '%';
        },
      },
    },
  };

  twoSetChartCurrencyOptions = {
    responsive: true,
    maintainAspectRatio: false,
    tooltips: {
      callbacks: {
        label: function (tooltipItem, data) {
          let index = tooltipItem.index;
          let setIndex = tooltipItem.datasetIndex;
          let currentValue = data.datasets[setIndex].data[index];

          if (setIndex == 1) return currentValue;

          return (
            '£' +
            (Math.abs(currentValue) > 999
              ? (Math.sign(currentValue) * (Math.abs(currentValue) / 1000)).toFixed(1) + 'k'
              : (Math.sign(currentValue) * Math.abs(currentValue)).toFixed(2))
          );
        },
        title: function (tooltipItem, data) {
          return data.datasets[tooltipItem[0].datasetIndex].label;
        },
      },
    },
    layout: {
      padding: {
        top: 25,
      },
    },
    legend: {
      position: 'bottom',
    },
    elements: {
      line: {
        tension: 0,
      },
    },
    scales: {
      yAxes: [
        {
          ticks: {
            //stepSize: this.baseStepSize,
            beginAtZero: true,
            padding: 15,
            //fontColor: 'green',
            //fontStyle: 'bold',
            offset: true,
            callback: function (value, index, values) {
              return value;
            },
          },
          type: 'linear', // only linear but allow scale type registration. This allows extensions to exist solely for log scale for instance
          display: true,
          position: 'right',
          id: 'y-axis-2',
        },
        {
          ticks: {
            beginAtZero: true,
            padding: 15,
            //fontColor: 'red',
            //fontStyle: 'bold',
            offset: true,
            callback: function (value, index, values) {
              let currency =
                '£' +
                (Math.abs(value) > 999
                  ? (Math.sign(value) * (Math.abs(value) / 1000)).toFixed(1) + 'k'
                  : (Math.sign(value) * Math.abs(value)).toFixed(2));
              return currency;
            },
          },
          gridLines: {
            display: false,
          },
          type: 'linear', // only linear but allow scale type registration. This allows extensions to exist solely for log scale for instance
          display: true,
          position: 'left',
          id: 'y-axis-1',
        },
      ],
      xAxes: [
        {
          ticks: {
            padding: 10,
          },
        },
      ],
    },
    plugins: {
      datalabels: {
        render: 'percentage',
        align: 'end',
        anchor: 'end',
        borderRadius: 4,
        backgroundColor: 'transparent',
        font: {
          weight: 'bold',
          size: 12,
        },
        formatter: (value, ctx) => {
          if (ctx.datasetIndex == 1) return value;

          let sum = 0;
          let dataArr = ctx.chart.data.datasets[0].data;
          dataArr.map((data) => {
            sum += data;
          });

          let currency =
            '£' +
            (Math.abs(value) > 999
              ? (Math.sign(value) * (Math.abs(value) / 1000)).toFixed(1) + 'k'
              : (Math.sign(value) * Math.abs(value)).toFixed(2));

          ctx.negative = value < 0;

          return currency;
        },
        color: (context) => {
          // Change the label text color based on our new `hovered` context value.
          return context.negative ? 'red' : this.greyText;
        },
      },
    },
  };

  selectedMonthRange = 6;
  monthRanges = [
    { label: 'Last 6 Months', value: 6 },
    { label: 'Last 12 Months', value: 12 },
    { label: 'Last 18 Months', value: 18 },
  ];
  currentStartDate: Date;
  currentEndDate: Date;

  monthlyTotals: any[];
  protected targetDataIndex: number = 0;

  public plugin: any;
  public MetricDisplayStyle = MetricDisplayStyle;
  csvStartDate: Date;
  csvEndDate: Date;
  minCSVDate: Date; // ensure date-range isn't too large (max 18 months)

  kpiStartDate: Date;
  minKPIDate: Date;

  lastRefreshTime: string;

  async ngOnInit() {
    this.plugin = ChartDataLabels;

    this.csvStartDate = moment().startOf('month').toDate();
    this.csvEndDate = moment().endOf('month').toDate();
    this.minCSVDate = moment().subtract(18, 'month').startOf('month').toDate();

    this.kpiStartDate = moment().toDate();
    this.minKPIDate = new Date(2018, 1, 1);

    this.kpiService.getKPILastCacheRefreshTime().then((response) => {
      this.lastRefreshTime = response;
    });
  }

  async loadData() {
    this.isLoading = true;
  }

  getMetricData_v2(
    title: string,
    dataIndex: number,
    diffRelativeToCompleted: boolean,
    showPercentageAsHeadline: boolean,
    cancelled: boolean,
    displayStyle: MetricDisplayStyle,
    includeExtraMetrics = false,
  ): any {
    let metricData = [];
    const monthlyData = cancelled ? this.cancelledTotals : this.completedTotals;

    monthlyData.forEach((data, index) => {
      let value = this.totalsChartData[dataIndex][index];
      let target = this.totalsChartData[this.targetDataIndex][index];
      let completed = data.totalCompleted;

      // get percentage of completed that correspond to the current filter
      let percOfComplete = completed > 0 ? parseFloat(((value / completed) * 100).toFixed(1)) : 0;
      //console.log("index = ", index, " - completed: ", completed, " - value: ", value, " - perc: ", percOfComplete);
      let percDiff = 0;

      if (!diffRelativeToCompleted && index > 0) {
        let prev = metricData[index - 1].value;
        if (!prev) {
          prev = 0;
        }

        if (prev > 0) {
          percDiff = ((value - prev) / prev) * 100.0;
        }
      } else if (index > 0) {
        let prev = metricData[index - 1].percOfComplete;
        if (!prev) {
          prev = 0;
        }

        percDiff = percOfComplete - prev;
      }

      if (isNaN(percDiff)) {
        percDiff = 0;
      }
      let label = percDiff.toFixed(1) + '%';
      if (percDiff > 0) {
        label = '+' + label;
      }

      let targetLabel = 'NA';
      if (target > 0) {
        const pT = (value / target) * 100;
        targetLabel = isNaN(pT) ? '' : pT.toFixed(1) + '%';
      }

      const mData = {
        date: moment(data.monthDate).format('MMMM YYYY'),
        value: value,
        completed: completed,
        percDiff: percDiff,
        percOfComplete: percOfComplete,
        percentLabel: label,
        target: target,
        targetPercentLabel: targetLabel,
        extraMetrics: includeExtraMetrics ? data.extraMetrics : null,
      };
      metricData.push(mData);
    });

    // last month first
    metricData = metricData.reverse();
    return {
      title,
      data: metricData,
      percAgainstComplete: diffRelativeToCompleted,
      showPercentageAsHeadline,
      displayStyle,
      showTarget: false,
      chartHeight: 300,
    };
  }

  getNoDataMetric(title: string) {
    return {
      title,
      data: [],
      percAgainstComplete: 0,
      showPercentageAsHeadline: false,
      displayStyle: MetricDisplayStyle.Number,
      chartHeight: 300,
      chartData: null,
      showTarget: false,
      noComp: false,
      totalName: '',
      useValueAsPercent: false,
      tooltip: '',
    };
  }

  getCalcPercentOptions() {
    let optionsCopy = _.cloneDeep(this.percentageTooltipChartOptions);

    optionsCopy.plugins.datalabels.formatter = function (value, ctx): string {
      let sum = 0;
      let dataArr = ctx.chart.data.datasets[0].data;
      dataArr.map((data) => {
        sum += data;
      });
      let percentage = (value / sum).toFixed(1) + '%';
      return percentage;
    };

    return optionsCopy;
  }

  getStackedPercentOptions() {
    let optionsCopy = _.cloneDeep(this.stackedChartOptions);

    optionsCopy.plugins.datalabels.formatter = function (value, ctx): string {
      if (ctx.datasetIndex == 0) {
        let value1 = ctx.chart.data.datasets[1].data[ctx.dataIndex];
        if (value1 == 0) {
          return '0%';
        }

        let vp = (value / value1) * 100;
        return isNaN(vp) ? '' : vp.toFixed(1) + '%';
      } else {
        return isNaN(value) ? '' : value;
      }
    };

    return optionsCopy;
  }

  getCompletedMovesChartData(dataIndex: number, targetIndex: number) {
    const totalMovesData = this.totalsChartData[dataIndex];
    const targetMovesData = this.totalsChartData[targetIndex];

    const maxKPI = Math.max(...totalMovesData);
    const maxTarget = Math.max(...targetMovesData);

    let totalMoves = totalMovesData.reduce((x, y) => x + y);
    let totalTargetMoves = targetMovesData.reduce((x, y) => x + y);

    const tt = (totalMoves / totalTargetMoves) * 100;
    let overallPercentLabel = tt.toFixed(1) + '%';

    // add some padding to y-axis
    //const maxY = Math.round(Math.max(maxKPI, maxTarget) / this.baseStepSize) * this.baseStepSize + this.baseStepSize;

    //this.baseChartOptions.scales.yAxes[0].ticks.suggestedMax = maxY;

    return {
      total: totalMoves,
      targetTotal: totalTargetMoves,
      overallPercentage: overallPercentLabel,
      chartType: 'bar',
      labels: this.monthDates,
      datasets: [
        {
          label: 'Completed Moves',
          data: totalMovesData,
          fill: false,
          backgroundColor: 'rgba(66, 164, 245, 0.5)',
          borderColor: this.dataBorderColor,
          borderWidth: this.dataBorderWidth,
          datalabels: {
            align: 'center',
            anchor: 'center',
            color: this.greyText,
            borderRadius: 4,
            font: {
              weight: 'bold',
            },
          },
        },
        {
          type: 'line',
          label: 'Target Completed Moves (to date)',
          data: targetMovesData,
          fill: false,
          backgroundColor: 'rgba(221, 0, 0, 0.5)',
          borderColor: 'rgba(221, 0, 0, 0.5)',
          borderWidth: 3,
          datalabels: {
            align: 'end',
            anchor: 'end',
            color: this.greyText,
            borderRadius: 4,
            font: {
              weight: 'bold',
            },
          },
        },
      ],
    };
  }

  getSingleChartData(
    dataIndex: number,
    chartColor: string,
    dataLabel: string,
    chartType: string = 'bar',
  ) {
    const data = this.totalsChartData[dataIndex];

    return {
      chartType: chartType,
      labels: this.monthDates,
      options: this.baseChartOptions,
      datasets: [
        {
          label: dataLabel,
          data: data,
          fill: true,
          borderColor: this.dataBorderColor,
          borderWidth: this.dataBorderWidth,
          backgroundColor: chartColor,
          hoverOffset: 4,
        },
      ],
    };
  }

  getCurrencyChartData(
    dataIndex: number,
    chartColor: string,
    dataLabel: string,
    chartType: string = 'bar',
  ) {
    const data = this.totalsChartData[dataIndex];
    const total = data.reduce((x, y) => x + y);

    let colors = [];
    for (let i = 0; i < data.length; i++) {
      let color = data[i] < 0 ? 'rgba(227, 43, 71, 0.9)' : chartColor;
      colors[i] = color;
    }

    return {
      totalCurrency: total,
      chartType: chartType,
      labels: this.monthDates,
      options: this.currencyChartOptions,
      datasets: [
        {
          label: dataLabel,
          data: data,
          fill: true,
          borderColor: this.dataBorderColor,
          borderWidth: this.dataBorderWidth,
          backgroundColor: colors,
          hoverOffset: 4,
        },
      ],
    };
  }

  getStackedChartData(
    dataIndex: number,
    secondDataIndex: number,
    chartColor: string,
    secondChartColor: string = null,
    dataLabel: string,
    secondDataLabel: string,
    chartType = 'bar',
    addTotal = true,
  ) {
    const data = this.totalsChartData[dataIndex];
    const total = data.reduce((x, y) => x + y);

    return {
      total: addTotal ? total : 0,
      chartType: chartType,
      labels: this.monthDates,
      options: this.stackedChartOptions,
      datasets: [
        {
          label: dataLabel,
          data: data,
          fill: true,
          backgroundColor: chartColor,
          borderColor: this.dataBorderColor,
          borderWidth: this.dataBorderWidth,
          datalabels: {
            align: 'center',
            anchor: 'end',
            borderRadius: 4,
            outsidePadding: 2,
            backgroundColor: 'rgba(51, 51, 51, 0.7)',
            color: 'white',
            font: {
              weight: 'bold',
            },
          },
        },
        {
          label: secondDataLabel,
          data: this.totalsChartData[secondDataIndex],
          fill: true,
          backgroundColor: secondChartColor, // 'rgba(45, 134, 51, 0.5)',
          borderColor: this.dataBorderColor,
          borderWidth: this.dataBorderWidth,
          datalabels: {
            align: 'end',
            anchor: 'end',
            borderRadius: 4,
            backgroundColor: 'transparent',
            font: {
              weight: 'bold',
            },
          },
        },
      ],
    };
  }

  getPercentageChartData(
    dataIndex: number,
    label: string,
    chartColor: string = 'rgba(35, 114, 195, 0.7)',
    chartType = 'bar',
    fillLine: boolean = true,
  ) {
    const data = this.totalsChartData[dataIndex];

    let colors = [];
    for (let i = 0; i < data.length; i++) {
      let color = data[i] < 0 ? 'rgba(227, 43, 71, 0.9)' : chartColor;
      colors[i] = color;
    }

    return {
      chartType: chartType,
      labels: this.monthDates,
      options: this.percentageTooltipChartOptions,
      datasets: [
        {
          label: label,
          data: data,
          fill: fillLine,
          borderColor: !fillLine ? chartColor : this.dataBorderColor,
          borderWidth: !fillLine ? 3 : this.dataBorderWidth,
          backgroundColor: colors,
          hoverOffset: 4,
        },
      ],
    };
  }

  getTwoSetChartData(
    dataIndex: number,
    secondDataIndex: number,
    chartColor: string,
    secondChartColor: string,
    dataLabel: string,
    secondDataLabel: string,
    axisColor: string = null,
    secondAxisColor: string = null,
    firstChartType = 'bar',
    secondChartType = 'line',
    chartOptions: any = null,
    percentageLabelOnFirst = false,
  ) {
    const data = this.totalsChartData[dataIndex];

    //const total = data.reduce((x, y) => x + y);

    let optionsCopy =
      chartOptions != null ? _.cloneDeep(chartOptions) : _.cloneDeep(this.twoSetChartOptions);

    //optionsCopy.scales.yAxes[0].ticks.fontColor = 'rgba(35, 114, 195, 0.7)';
    //optionsCopy.scales.yAxes[1].ticks.fontColor = axisColor || chartColor;

    let ret: any = {
      //total: total,
      labels: this.monthDates,
      options: optionsCopy,
      chartType: firstChartType,
      datasets: [
        {
          label: dataLabel,
          type: firstChartType,
          data: data,
          lineTension: 0,
          spanGaps: false,
          fill: firstChartType == 'bar',
          yAxisID: 'y-axis-1',
          backgroundColor: chartColor,
          borderColor: firstChartType == 'bar' ? this.dataBorderColor : chartColor,
          borderWidth: firstChartType == 'bar' ? this.dataBorderWidth : 3,
          datalabels: {
            align: 'bottom',
            anchor: 'center',
            color: (context) => {
              let color = this.greyText;
              return color;
            },
            backgroundColor: 'transparent',
            font: {
              weight: 'bold',
            },
          },
        },
        secondDataIndex >= 0
          ? {
              type: secondChartType,
              label: secondDataLabel,
              data: this.totalsChartData[secondDataIndex],
              lineTension: 0,
              spanGaps: false,
              fill: secondChartType == 'bar',
              yAxisID: 'y-axis-2',
              backgroundColor: secondChartColor,
              borderColor: secondChartType == 'bar' ? this.dataBorderColor : secondChartColor,
              borderWidth: secondChartType == 'bar' ? this.dataBorderWidth : 3,
              datalabels: {
                align: secondChartType == 'bar' ? 'bottom' : 'top',
                anchor: secondChartType == 'bar' ? 'center' : 'start',
                color: (context) => {
                  let color = this.greyText;
                  return color;
                },
                backgroundColor: 'transparent',
                font: {
                  weight: 'bold',
                },
              },
            }
          : {},
      ],
    };

    if (percentageLabelOnFirst) {
      ret.datasets[0].datalabels.formatter = (value, ctx) => {
        return percentageLabelOnFirst ? value.toFixed(1) + '%' : value;
      };
    }

    return ret;
  }

  getTwoSetOneScaleChartData(
    dataIndex: number,
    secondDataIndex: number,
    chartColor: string,
    secondChartColor: string,
    dataLabel: string,
    secondDataLabel: string,
  ) {
    const data = this.totalsChartData[dataIndex];

    return {
      labels: this.monthDates,
      options: this.twoSetOneScaleChartOptions,
      chartType: 'bar',
      datasets: [
        {
          label: dataLabel,
          type: 'bar',
          data: data,
          spanGaps: false,
          fill: true,
          backgroundColor: chartColor,
          borderColor: chartColor,
          borderWidth: this.dataBorderWidth,
          datalabels: {
            align: 'center',
            anchor: 'center',
            color: (context) => {
              let color = this.greyText;
              return color;
            },
            backgroundColor: 'transparent',
            font: {
              weight: 'bold',
            },
          },
        },
        secondDataIndex >= 0
          ? {
              type: 'bar',
              label: secondDataLabel,
              data: this.totalsChartData[secondDataIndex],
              spanGaps: false,
              fill: true,
              backgroundColor: secondChartColor,
              borderColor: secondChartColor,
              borderWidth: this.dataBorderWidth,
              datalabels: {
                align: 'center',
                anchor: 'center',
                color: (context) => {
                  let color = this.greyText;
                  return color;
                },
                backgroundColor: 'transparent',
                font: {
                  weight: 'bold',
                },
              },
            }
          : {},
      ],
    };
  }

  getPerformanceChartData(completedIndex: number, onTime, isLatePerformance = false) {
    const totalA = onTime.reduce((x, y) => x + y);

    return {
      total: totalA,
      chartType: 'bar',
      labels: this.monthDates,
      options: this.baseChartOptions,
      datasets: [
        {
          label: 'Completed Moves',
          data: this.totalsChartData[completedIndex],
          fill: true,
          backgroundColor: 'rgba(10, 220, 85, 0.5)',
          borderColor: this.dataBorderColor,
          borderWidth: this.dataBorderWidth,
        },
        {
          label: isLatePerformance ? '< 1hr' : 'On Time',
          data: onTime,
          fill: true,
          backgroundColor: 'rgba(0, 128, 203, 0.7)',
          borderColor: this.dataBorderColor,
          borderWidth: this.dataBorderWidth,
        },
      ],
    };
  }

  getSupplierTasksChartData(dataIndex: number, completedIndex: number) {
    const mappedData = this.totalsChartData[dataIndex];

    const total = mappedData.reduce((x, y) => {
      return x + y;
    });
    const chartColor = 'rgba(10, 220, 85, 0.5)';

    // console.log("TPAS Metric: ", metric, " - total for supplier data: ", total);

    let optionsCopy = _.cloneDeep(this.twoSetChartOptions);

    //optionsCopy.scales.yAxes[0].ticks.fontColor = 'rgba(35, 114, 195, 0.7)';
    //optionsCopy.scales.yAxes[1].ticks.fontColor = chartColor;

    return {
      total: total,
      chartType: 'bar',
      labels: this.monthDates,
      options: optionsCopy,
      datasets: [
        {
          label: 'Tasks Per Active Supplier',
          type: 'bar',
          data: mappedData,
          fill: true,
          yAxisID: 'y-axis-1',
          backgroundColor: chartColor,
          borderColor: this.dataBorderColor,
          borderWidth: this.dataBorderWidth,
          datalabels: {
            align: 'top',
            anchor: 'start',
            color: this.greyText,
            backgroundColor: 'transparent',
            font: {
              weight: 'bold',
            },
          },
        },
        {
          type: 'line',
          label: 'Completed Moves',
          data: this.totalsChartData[completedIndex],
          fill: false,
          yAxisID: 'y-axis-2',
          backgroundColor: 'rgba(66, 164, 245, 0.5)',
          borderColor: 'rgba(66, 164, 245, 0.5)',
          borderWidth: 3,
          datalabels: {
            align: 'top',
            anchor: 'start',
            color: this.greyText,
            backgroundColor: 'transparent',
            font: {
              weight: 'bold',
            },
          },
        },
      ],
    };
  }

  setMonthDates(rangeInMonths: number) {
    this.monthDates = [];

    let endDate = moment(this.kpiStartDate).endOf('month');
    let startDate = moment(endDate).subtract(rangeInMonths, 'M').startOf('month');
    let currentDate = moment(startDate.toDate());

    while (currentDate <= endDate) {
      this.monthDates.push(currentDate.format('MMM YYYY'));
      currentDate.add(1, 'M');
    }

    this.currentStartDate = startDate.toDate();
    this.currentEndDate = endDate.toDate();
  }

  selectedChartData(monthlyMetric, metric, event) {
    const monthIndex = event.element._index;
    const dataIndex = event.element._datasetIndex;

    console.log('Selected chart data-point: ', event);
  }

  onDateMonthChanged(selectedDate: Date) {
    // select last day of the month
    this.kpiStartDate = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 0);
    console.log('New range selected: ', this.kpiStartDate);
    this.loadData();
  }

  onDateRangeChanged(event) {
    console.log('New range selected: ', event);
    this.loadData();
  }

  async liveFeedOptionChanged(event: any) {}

  async dailyLiveFeedOptionChanged(event: any) {}

  async weeklyLiveFeedOptionChanged($event: any) {}

  getCurrencySummaryValue(num: any) {
    return (
      '£' +
      (Math.abs(num) > 999
        ? (Math.sign(num) * (Math.abs(num) / 1000)).toFixed(1) + 'k'
        : (Math.sign(num) * Math.abs(num)).toFixed(2))
    );
  }

  getCurrencyValue(num: any) {
    return num.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' });
  }

  exportCSV() {
    this.csvDownloading = true;
    this.kpiService.downloadToCSV(this.csvStartDate, this.csvEndDate, true).then(() => {
      this.csvDownloading = false;
    });
  }
}
